import { type AxiosResponse, HttpStatusCode } from 'axios';

import { userApi } from '@/api/generatedApi';
import {
    getAccessToken,
    getActiveAccountInfo,
    hasAuthenticatedAccount,
    loginWithRedirect,
    logoutWithRedirect,
    preHandleAuthRedirect,
} from '@/auth/authUtils';
import { type CurrentUserResponse, UserRole } from '@/generated';
import { consumeRedirectAfterLogin, getRedirectAfterLogin } from '@/utils/localStorageUtil';
import type { AccountInfo } from '@azure/msal-browser';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const useAuthStore = defineStore('auth', () => {
    const isAuthenticated = ref<boolean>(false);
    const currentAccount = ref<AccountInfo | undefined>(undefined);
    const user = ref<CurrentUserResponse | undefined>(undefined);
    const userRole = ref<UserRole>(UserRole.None);
    const hasLoadedUserRole = ref<boolean>(false);
    const roleLoadedForAccountId = ref<string | undefined>(undefined);
    let userRolePromise: Promise<void> | undefined;
    let roleRequestedForAccountId: string | undefined;

    const getAccountCacheKey = (account: AccountInfo | undefined): string | undefined =>
        account?.homeAccountId ?? account?.localAccountId ?? account?.username ?? undefined;

    const resetRoleCache = (): void => {
        user.value = undefined;
        userRole.value = UserRole.None;
        hasLoadedUserRole.value = false;
        roleLoadedForAccountId.value = undefined;
    };

    const setCurrentAccount = (account: AccountInfo | undefined): void => {
        const previousAccountId = getAccountCacheKey(currentAccount.value);
        const nextAccountId = getAccountCacheKey(account);

        if (previousAccountId !== nextAccountId) {
            resetRoleCache();
        }

        currentAccount.value = account;
    };

    const syncAuthStateFromMsal = async () => {
        try {
            await preHandleAuthRedirect();
            isAuthenticated.value = await hasAuthenticatedAccount();
            setCurrentAccount(await getActiveAccountInfo());
            if (isAuthenticated.value) {
                consumeRedirectAfterLogin();
                await loadUserRole();
            }
        } catch {
            isAuthenticated.value = false;
            setCurrentAccount(undefined);
        }
    };

    const loadUserRole = async (): Promise<void> => {
        const accountId = getAccountCacheKey(currentAccount.value);
        if (!accountId) {
            resetRoleCache();
            return;
        }

        if (hasLoadedUserRole.value && roleLoadedForAccountId.value === accountId) {
            return;
        }

        if (userRolePromise && roleRequestedForAccountId === accountId) {
            await userRolePromise;
            return;
        }

        const requestedAccountId = accountId;
        const requestPromise = (async (): Promise<void> => {
            try {
                const response: AxiosResponse<CurrentUserResponse> =
                    await userApi.apiV1UserWhoAmIGet();
                if (requestedAccountId !== getAccountCacheKey(currentAccount.value)) {
                    return;
                }

                if (
                    // If the response status indicates an error, reset the role cache
                    response.status < HttpStatusCode.Ok ||
                    response.status >= HttpStatusCode.MultipleChoices
                ) {
                    resetRoleCache();
                    return;
                }

                user.value = response.data;
                userRole.value = response.data.role ?? UserRole.None;
                hasLoadedUserRole.value = true;
                roleLoadedForAccountId.value = requestedAccountId;
            } catch {
                if (requestedAccountId !== getAccountCacheKey(currentAccount.value)) {
                    return;
                }

                resetRoleCache();
            }
        })();

        userRolePromise = requestPromise;
        roleRequestedForAccountId = requestedAccountId;
        try {
            await requestPromise;
        } finally {
            if (userRolePromise === requestPromise) {
                userRolePromise = undefined;
                roleRequestedForAccountId = undefined;
            }
        }
    };

    void syncAuthStateFromMsal();

    const isAdmin = computed(() => userRole.value.includes(UserRole.Admin));

    const trySilentLogins = async (): Promise<AccountInfo | undefined> => {
        const token = await getAccessToken();
        isAuthenticated.value = !!token;
        if (!token) {
            setCurrentAccount(undefined);
            return undefined;
        }

        setCurrentAccount(await getActiveAccountInfo());
        return currentAccount.value;
    };

    const explicitLogin = async (): Promise<void> => {
        const redirectStartPage = getRedirectAfterLogin() ?? '/';
        await loginWithRedirect(redirectStartPage);
    };

    const logout = async () => {
        isAuthenticated.value = false;
        setCurrentAccount(undefined);
        resetRoleCache();
        await logoutWithRedirect();
    };

    const getToken = async (): Promise<string> => {
        const token = await getAccessToken();
        isAuthenticated.value = !!token;
        setCurrentAccount(token ? await getActiveAccountInfo() : undefined);
        return token;
    };

    const getAccountInfo = (): AccountInfo | undefined => currentAccount.value;

    return {
        logout,
        getToken,
        explicitLogin,
        trySilentLogins,
        getAccountInfo,
        syncAuthStateFromMsal,
        loadUserRole,
        isAuthenticated,
        isAdmin,
        user,
    };
});

export default useAuthStore;
